import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, first, map, mergeMap, Observable, of, Subject, takeUntil, tap, zip } from 'rxjs';

import { LgvClientService, LgvConstants, LgvUserService, ROUTE_ANIMATIONS_ELEMENTS } from '../../../core/core.module';
import { LgvAuthMgrService } from '../../../core/services/lgv-auth-mgr.service';
import { LgvCompanyService } from '../../../core/services/lgv-company.service';
import { DisclosureType } from '../../../core/utility/lgv-constants';
import { LgvModel } from '../../../data-models/longevity.model';

@Component({
    selector: 'lgv-universal-footer',
    templateUrl: './universal-footer.component.html',
    styleUrls: ['./universal-footer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UniversalFooterComponent implements OnInit, OnDestroy, OnChanges {
    routeAnimationsElements = ROUTE_ANIMATIONS_ELEMENTS;

    @Input() companyCodeParam: string;
    @Input() isCobrandedFooter: boolean = false;

    onDestroy$ = new Subject();

    _companyInfo: LgvModel.CompanyInfo = null;
    companyInfo$ = new BehaviorSubject(this._companyInfo);

    _branding: LgvModel.CompanyBranding = null;
    branding$ = new BehaviorSubject(this._branding);

    _clientDisclosures: LgvModel.ClientDisclosures = null;
    clientDisclosures$ = new BehaviorSubject(this._clientDisclosures);

    get isMortgageCompany$(): Observable<boolean> { return this.companyInfo$.pipe(first(f => f !== null), map(x => { return LgvConstants.isMortgageLenderBusinessType(x?.businessTypeId) })) }
    get linkFooterDisclosures$(): Observable<LgvModel.Disclosure[]> { return this.dynamicDisclosures$.pipe(map(m => m.filter(f => this.lgvConstants.isLinkFooterDisclaimer(f)))) }
    get textFooterDisclosures$(): Observable<LgvModel.Disclosure[]> { return this.dynamicDisclosures$.pipe(map(m => m.filter(f => this.lgvConstants.isTextFooterDisclaimer(f)))) }

    _dynamicDisclosures: LgvModel.Disclosure[] = [];
    dynamicDisclosures$ = new BehaviorSubject(this._dynamicDisclosures);

    constructor(
        private lgvUserSvc: LgvUserService,
        public lgvConstants: LgvConstants,
        public lgvAuthMgr: LgvAuthMgrService,
        public lgvClientSvc: LgvClientService,
        public lgvCompanySvc: LgvCompanyService
    ) { }

    ngOnChanges(changes: SimpleChanges) {

        if (!!changes.companyCodeParam) {
            let newVal = changes.companyCodeParam.currentValue;
            //let oldVal = changes.companyCodeParam.previousValue;
            //console.log('');
            //console.log(`********** Changes to CompanyCode  isCB: ${this.isCobrandedFooter}  |  ${oldVal} =>  ${newVal} **************`);
            //console.log('');
            //console.log(changes);
            this.fetchCompanyInfo(newVal);
            this.fetchBranding(newVal);
        }
    }

    ngOnInit() {

        // for anonymous or Auth0 users, use the disclosures from the company code (in branding$)
        this.branding$.pipe(filter(f => !!f), takeUntil(this.onDestroy$)).subscribe(b => {
            if (!this.lgvAuthMgr.IsClient) {
                let nonClientDisclosures = b.disclosures.filter(d => !(!!d.isForCobrandedClientOnly));
                this.dynamicDisclosures$.next(nonClientDisclosures);
            }
        });


        this.lgvAuthMgr.IsClient$.pipe(first(f => !!f), takeUntil(this.onDestroy$)).subscribe(isClient => {
            if (isClient) {
                // disclosures are driven by client agent / company / co-branding.
                // Once we have the clientID, then fetch the disclosures for both companies (agent company and co-branded company)
                this.lgvClientSvc.ClientID$.pipe(
                    first(f => !!f && f !== 'Loading'),
                    takeUntil(this.onDestroy$),
                    mergeMap((clientId) => {
                        return this.lgvClientSvc.getClientDisclosures(clientId);
                    })
                ).subscribe(x => {
                    if (this.isCobrandedFooter) {
                        this.dynamicDisclosures$.next(x.coBrandedAgentDisclosures.disclosures);
                    }
                    else {
                        this.dynamicDisclosures$.next(x.agentDisclosures.disclosures);
                    }

                }, (err) => {
                    console.log(err);
                });
            }
        });
    }

    ngOnDestroy(): void {
        this.onDestroy$.complete();
    }

    fetchCompanyInfo(companyCode: string) {
        this.lgvCompanySvc.getCompanyInfoByCode(companyCode).pipe(first(), takeUntil(this.onDestroy$)).subscribe(x => {
            this.companyInfo$.next(x);
        });

    }

    fetchBranding(companyCode: string) {
        this.lgvCompanySvc.getBrandingByCode(companyCode).pipe(first(), takeUntil(this.onDestroy$)).subscribe(x => {
            this.branding$.next(x);
        });
    }


    public socialMediaNameFromID(key: string): string {
        return LgvConstants.SocialMediaNameFromID(key);
    }

}
